import {
  AdMob,
  AdmobConsentStatus,
  BannerAdPosition,
  BannerAdSize,
  RewardAdPluginEvents,
} from "@capacitor-community/admob";
import { Capacitor } from "@capacitor/core";
import { reportError } from "./crash";

const GOOGLE_TEST = "3940256099942544";

/** Official Google demo units — fill on TestFlight before App Store listing. */
const DEMO_IOS = {
  bannerUnitId: `ca-app-pub-${GOOGLE_TEST}/2934735716`,
  interstitialUnitId: `ca-app-pub-${GOOGLE_TEST}/4411468910`,
  rewardedUnitId: `ca-app-pub-${GOOGLE_TEST}/1712485313`,
};

/** Bundled fallbacks — also served / overridable via GET /api/ads/units (no TF rebuild). */
const FALLBACK = {
  appIdIos: "ca-app-pub-9309754113951914~7396909714",
  bannerUnitId: "ca-app-pub-9309754113951914/5349330877",
  interstitialUnitId: "ca-app-pub-9309754113951914/8518419698",
  rewardedUnitId: "ca-app-pub-9309754113951914/9958925654",
  interstitialEvery: 3,
  useTestAds: false,
};

type AdUnits = {
  appIdIos: string;
  bannerUnitId: string;
  interstitialUnitId: string;
  rewardedUnitId: string;
  interstitialEvery: number;
  useTestAds: boolean;
};

/** Live units (mutated after remote fetch). */
export const ADMOB: AdUnits = { ...FALLBACK };

function isTestUnit(id: string) {
  return !id || id.includes(GOOGLE_TEST);
}

function applyTestOverlay() {
  if (!ADMOB.useTestAds) return;
  ADMOB.bannerUnitId = DEMO_IOS.bannerUnitId;
  ADMOB.interstitialUnitId = DEMO_IOS.interstitialUnitId;
  ADMOB.rewardedUnitId = DEMO_IOS.rewardedUnitId;
}

function apiBase() {
  const raw = import.meta.env.VITE_API_URL as string | undefined;
  if (typeof raw === "string" && raw.trim()) return raw.replace(/\/$/, "");
  if (import.meta.env.DEV) return "";
  return "https://worldvelopers.com/3dash";
}

/** Pull units from API — change server/ads-units.json without rebuilding the IPA. */
async function refreshUnitsFromServer() {
  try {
    const r = await fetch(`${apiBase()}/api/ads/units`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!r.ok) return;
    const data = (await r.json()) as Partial<AdUnits>;
    if (typeof data.useTestAds === "boolean") {
      ADMOB.useTestAds = data.useTestAds;
    }
    if (typeof data.bannerUnitId === "string" && data.bannerUnitId.includes("/")) {
      ADMOB.bannerUnitId = data.bannerUnitId.trim();
    }
    if (
      typeof data.interstitialUnitId === "string" &&
      data.interstitialUnitId.includes("/")
    ) {
      ADMOB.interstitialUnitId = data.interstitialUnitId.trim();
    }
    if (
      typeof data.rewardedUnitId === "string" &&
      data.rewardedUnitId.includes("/")
    ) {
      ADMOB.rewardedUnitId = data.rewardedUnitId.trim();
    }
    if (
      typeof data.interstitialEvery === "number" &&
      data.interstitialEvery >= 1 &&
      data.interstitialEvery <= 20
    ) {
      ADMOB.interstitialEvery = Math.floor(data.interstitialEvery);
    }
    applyTestOverlay();
  } catch {
    applyTestOverlay();
  }
}

let initialized = false;
let initPromise: Promise<void> | null = null;
let bannerVisible = false;
let rewardedReady = false;
let rewardedPreparedAt = 0;
let preloadPromise: Promise<boolean> | null = null;
/** AdMob rewarded creatives go stale; don't show a menu preload from a long run. */
const REWARDED_FRESH_MS = 90_000;

/** EEA/UK users need a Google UMP consent form before ads can be requested. */
let canRequestAds = true;
/** GDPR requires a persistent way to re-open consent choices (Settings link). */
let privacyOptionsRequired = false;

export function adsCanRequest() {
  return canRequestAds;
}

export function adsPrivacyOptionsRequired() {
  return privacyOptionsRequired;
}

/** "test" | "live" — for Settings status line. */
export function adsModeLabel(): "test" | "live" {
  return ADMOB.useTestAds || isTestUnit(ADMOB.bannerUnitId) ? "test" : "live";
}

/** Open Google's "Privacy options" form — wire to a Settings button (GDPR requirement). */
export async function openAdPrivacyOptions(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await AdMob.showPrivacyOptionsForm();
    await gatherConsent();
    if (canRequestAds && !initialized) {
      initPromise = null;
      await initAds();
    } else if (canRequestAds) {
      void preloadRewardedAd();
    }
  } catch (e) {
    console.warn("[ads] privacy options form failed", e);
  }
}

/** Google UMP consent gathering — required before requesting ads in EEA/UK. */
async function gatherConsent(): Promise<void> {
  try {
    const info = await AdMob.requestConsentInfo();
    if (
      info.status === AdmobConsentStatus.REQUIRED &&
      info.isConsentFormAvailable
    ) {
      const after = await AdMob.showConsentForm();
      canRequestAds = after.canRequestAds;
      privacyOptionsRequired =
        (after.privacyOptionsRequirementStatus as unknown as string) ===
        "REQUIRED";
      return;
    }
    canRequestAds = info.canRequestAds;
    privacyOptionsRequired =
      (info.privacyOptionsRequirementStatus as unknown as string) ===
      "REQUIRED";
  } catch (e) {
    console.warn("[ads] consent gather failed — defaulting to allow", e);
    canRequestAds = true;
  }
}

export function initAds(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve();
  if (initPromise) return initPromise;
  initPromise = (async () => {
    await refreshUnitsFromServer();
    applyTestOverlay();
    await gatherConsent();
    if (!canRequestAds) {
      console.info("[ads] consent not granted — ads withheld");
      return;
    }
    try {
      await AdMob.requestTrackingAuthorization();
    } catch {
      /* older iOS / denied */
    }
    try {
      const testing =
        ADMOB.useTestAds ||
        isTestUnit(ADMOB.bannerUnitId) ||
        isTestUnit(ADMOB.appIdIos);
      await AdMob.initialize({
        initializeForTesting: testing,
        testingDevices: [],
      });
      initialized = true;
      console.info("[ads] ready", {
        mode: adsModeLabel(),
        banner: isTestUnit(ADMOB.bannerUnitId) ? "test" : "prod",
        interstitial: isTestUnit(ADMOB.interstitialUnitId) ? "test" : "prod",
        rewarded: isTestUnit(ADMOB.rewardedUnitId) ? "test" : "prod",
        every: ADMOB.interstitialEvery,
      });
      void preloadRewardedAd();
    } catch (e) {
      console.warn("[ads] init failed", e);
      reportError(e, "ads.init");
      initPromise = null;
    }
  })();
  return initPromise;
}

function rewardedOptions() {
  return {
    adId: ADMOB.rewardedUnitId,
    isTesting: ADMOB.useTestAds || isTestUnit(ADMOB.rewardedUnitId),
  };
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

function rewardedIsFresh() {
  return rewardedReady && Date.now() - rewardedPreparedAt < REWARDED_FRESH_MS;
}

/** Keep a rewarded video warm so Continue isn't a cold AdMob load. */
export async function preloadRewardedAd(opts?: {
  force?: boolean;
}): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true;
  if (!initialized) await initAds();
  if (!canRequestAds || !initialized) return false;
  // Don't abort an in-flight prepare (Continue overlay warms while the player reads).
  if (preloadPromise) return preloadPromise;
  const stale = rewardedReady && !rewardedIsFresh();
  if (opts?.force || stale) {
    rewardedReady = false;
  }
  if (rewardedReady) return true;
  preloadPromise = (async () => {
    try {
      await AdMob.prepareRewardVideoAd(rewardedOptions());
      rewardedReady = true;
      rewardedPreparedAt = Date.now();
      return true;
    } catch (e) {
      const msg = String((e as Error)?.message ?? e);
      // Plugin often errors if a video is already in memory — treat as ready.
      if (/already|prepared|is loaded|loaded/i.test(msg)) {
        rewardedReady = true;
        rewardedPreparedAt = Date.now();
        return true;
      }
      rewardedReady = false;
      console.warn("[ads] rewarded preload failed", e);
      return false;
    } finally {
      preloadPromise = null;
    }
  })();
  return preloadPromise;
}

/** Banner on menu / result — hidden while playing. */
export async function showMenuBanner(removeAds: boolean) {
  if (!Capacitor.isNativePlatform() || removeAds) {
    await hideMenuBanner();
    return;
  }
  if (!initialized) await initAds();
  if (!canRequestAds) return;
  try {
    await AdMob.showBanner({
      adId: ADMOB.bannerUnitId,
      // Fixed 50pt strip sits lower than adaptive (which can be ~100pt and
      // covers the Dash button). Menu CSS reserves the same height.
      adSize: BannerAdSize.BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
      isTesting: ADMOB.useTestAds || isTestUnit(ADMOB.bannerUnitId),
    });
    bannerVisible = true;
  } catch (e) {
    console.warn("[ads] banner failed", e);
  }
}

export async function hideMenuBanner() {
  if (!Capacitor.isNativePlatform() || !bannerVisible) return;
  try {
    await AdMob.hideBanner();
  } catch {
    try {
      await AdMob.removeBanner();
    } catch {
      /* ignore */
    }
  }
  bannerVisible = false;
}

export type RewardedFail = "consent" | "no_fill" | "closed" | "error";

export type RewardedPlay = { ok: boolean; reason?: RewardedFail };

async function showPreparedRewarded(): Promise<RewardedPlay> {
  const result = await new Promise<RewardedPlay>((resolve) => {
    let earned = false;
    let done = false;
    const handles: { remove: () => Promise<void> }[] = [];
    const finish = (value: RewardedPlay) => {
      if (done) return;
      done = true;
      for (const h of handles) void h.remove();
      resolve(value);
    };

    void (async () => {
      try {
        const [hReward, hFail, hDismiss] = await Promise.all([
          AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
            earned = true;
          }),
          AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => {
            finish({ ok: false, reason: "error" });
          }),
          AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
            finish(earned ? { ok: true } : { ok: false, reason: "closed" });
          }),
        ]);
        if (done) {
          void hReward.remove();
          void hFail.remove();
          void hDismiss.remove();
          return;
        }
        handles.push(hReward, hFail, hDismiss);
        const item = await AdMob.showRewardVideoAd();
        if (item && Number(item.amount) > 0) earned = true;
      } catch (e) {
        console.warn("[ads] rewarded show failed", e);
        finish({ ok: false, reason: "error" });
      }
    })();

    setTimeout(() => {
      if (!done) finish(earned ? { ok: true } : { ok: false, reason: "error" });
    }, 120000);
  });
  void preloadRewardedAd({ force: true });
  return result;
}

/**
 * Play a rewarded video. Fresh-prepares after hiding the banner — a warm ad
 * from the menu often expires mid-run, which showed "Ad unavailable".
 */
export async function playRewardedAd(): Promise<RewardedPlay> {
  if (!Capacitor.isNativePlatform()) {
    await sleep(450);
    return { ok: true };
  }
  if (!initialized) await initAds();
  if (!canRequestAds) return { ok: false, reason: "consent" };

  await hideMenuBanner();
  await sleep(400);

  const prepare = async (force: boolean) => {
    let loaded = await preloadRewardedAd({ force });
    if (!loaded) {
      await sleep(1500);
      loaded = await preloadRewardedAd({ force: true });
    }
    return loaded;
  };

  if (!(await prepare(!rewardedIsFresh()))) {
    reportError(new Error("rewarded no_fill"), "ads.rewarded.prepare");
    return { ok: false, reason: "no_fill" };
  }

  rewardedReady = false;
  const first = await showPreparedRewarded();
  if (first.ok || first.reason === "closed") return first;

  await sleep(700);
  if (!(await prepare(true))) return first;
  rewardedReady = false;
  return showPreparedRewarded();
}

/** Returns true if reward was earned (or web mock succeeds). */
export async function showRewardedContinue(): Promise<boolean> {
  return (await playRewardedAd()).ok;
}

/**
 * Interstitial only every N finished runs (N from remote config).
 * Returns the new counter value to persist.
 */
export async function maybeShowInterstitial(
  removeAds: boolean,
  runsSinceAd: number,
): Promise<number> {
  const next = runsSinceAd + 1;
  if (removeAds) return next;
  if (next < ADMOB.interstitialEvery) return next;

  if (!Capacitor.isNativePlatform()) {
    console.info("[ads] interstitial cadence hit (web skip)");
    return 0;
  }
  if (!initialized) await initAds();
  if (!canRequestAds) return 0;
  try {
    await hideMenuBanner();
    await AdMob.prepareInterstitial({
      adId: ADMOB.interstitialUnitId,
      isTesting: ADMOB.useTestAds || isTestUnit(ADMOB.interstitialUnitId),
    });
    await AdMob.showInterstitial();
  } catch {
    /* ignore */
  }
  return 0;
}
